import React, { useContext, useEffect, useMemo, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TicketsQueueSelect from "../../components/TicketsQueueSelect";
import usePaginatedUsers from "../../hooks/usePaginatedUsers";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  container: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden"
  },
  gridRoot: {
    width: "100%",
    margin: 0
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 320
  },
  customFixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 120
  },
  filterPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  filterControl: {
    minWidth: 180
  },
  rankingTable: {
    width: "100%",
    borderCollapse: "collapse"
  },
  rankingHeaderCell: {
    textAlign: "left",
    fontWeight: 600,
    padding: theme.spacing(1),
    borderBottom: "1px solid #ddd"
  },
  rankingCell: {
    padding: theme.spacing(1),
    borderBottom: "1px solid #eee",
    wordBreak: "break-word"
  }
}));

const todayIso = () => new Date().toISOString().slice(0, 10);

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const userQueueIds = useMemo(
    () => (user.queues && user.queues.length > 0 ? user.queues.map(q => q.id) : []),
    [user.queues]
  );

  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [selectedSellerId, setSelectedSellerId] = useState("");
  const {
    users,
    loading: loadingUsers,
    hasMore: usersHasMore,
    menuProps: usersMenuProps
  } = usePaginatedUsers();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totals: { total: 0, open: 0, pending: 0, closed: 0 },
    queueBreakdown: [],
    tagBreakdown: [],
    ranking: []
  });

  const usersFromSelectedQueues = users.filter(userItem => {
    if (!selectedQueueIds.length) return true;
    if (!Array.isArray(userItem.queues)) return false;
    return userItem.queues.some(queue => selectedQueueIds.includes(queue.id));
  });

  useEffect(() => {
    if (!selectedSellerId || selectedSellerId === "unassigned") return;
    if (usersHasMore || loadingUsers) return;

    const stillAvailable = usersFromSelectedQueues.some(
      userItem => String(userItem.id) === String(selectedSellerId)
    );

    if (!stillAvailable) {
      setSelectedSellerId("");
    }
  }, [selectedQueueIds, selectedSellerId, usersFromSelectedQueues, usersHasMore, loadingUsers]);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dashboard/metrics", {
          params: {
            startDate,
            endDate,
            sellerId: selectedSellerId || undefined,
            queueIds: JSON.stringify(selectedQueueIds)
          }
        });

        setMetrics({
          totals: data?.totals || { total: 0, open: 0, pending: 0, closed: 0 },
          queueBreakdown: Array.isArray(data?.queueBreakdown)
            ? data.queueBreakdown
            : [],
          tagBreakdown: Array.isArray(data?.tagBreakdown)
            ? data.tagBreakdown
            : Array.isArray(data?.tagsBreakdown)
              ? data.tagsBreakdown
              : [],
          ranking: Array.isArray(data?.ranking) ? data.ranking : []
        });
      } catch (_error) {
        setMetrics({
          totals: { total: 0, open: 0, pending: 0, closed: 0 },
          queueBreakdown: [],
          tagBreakdown: [],
          ranking: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [startDate, endDate, selectedSellerId, selectedQueueIds]);

  return (
    <div className={classes.container}>
      <MainHeader>
        <Title description="Acompanhe volume, desempenho e distribuição dos atendimentos.">
          Dashboard
        </Title>
      </MainHeader>
      <Grid container spacing={3} className={classes.gridRoot}>
        <Grid item xs={12}>
          <Paper className={classes.filterPaper}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Data Inicial"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Data Final"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" size="small" fullWidth className={classes.filterControl}>
                  <InputLabel id="seller-filter-label">Usuário</InputLabel>
                  <Select
                    labelId="seller-filter-label"
                    value={selectedSellerId}
                    onChange={e => setSelectedSellerId(e.target.value)}
                    label="Vendedor"
                    MenuProps={usersMenuProps}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    <MenuItem value="unassigned">
                      <em>Nao atribuido</em>
                    </MenuItem>
                    {usersFromSelectedQueues.map(userItem => (
                      <MenuItem key={userItem.id} value={userItem.id}>
                        {userItem.name}
                      </MenuItem>
                    ))}
                    {loadingUsers && (
                      <MenuItem disabled>
                        <CircularProgress size={18} />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
               <Grid item xs={12} md={3}>
                <TicketsQueueSelect
                  selectedQueueIds={selectedQueueIds}
                  userQueues={user?.queues}
                  onChange={values => setSelectedQueueIds(values)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
            <Typography component="h3" variant="h6" color="primary" paragraph>
              {i18n.t("dashboard.messages.inAttendance.title")}
            </Typography>
            <Grid item>
              <Typography component="h1" variant="h4">
                {loading ? <CircularProgress size={24} /> : metrics.totals.open}
              </Typography>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
            <Typography component="h3" variant="h6" color="primary" paragraph>
              {i18n.t("dashboard.messages.waiting.title")}
            </Typography>
            <Grid item>
              <Typography component="h1" variant="h4">
                {loading ? <CircularProgress size={24} /> : metrics.totals.pending}
              </Typography>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
            <Typography component="h3" variant="h6" color="primary" paragraph>
              {i18n.t("dashboard.messages.closed.title")}
            </Typography>
            <Grid item>
              <Typography component="h1" variant="h4">
                {loading ? <CircularProgress size={24} /> : metrics.totals.closed}
              </Typography>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.fixedHeightPaper}>
            <Typography component="h3" variant="h6" color="primary" paragraph>
              Atendimentos por Departamento
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <table className={classes.rankingTable}>
                <thead>
                  <tr>
                    <th className={classes.rankingHeaderCell}>Departamento</th>
                    <th className={classes.rankingHeaderCell}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.queueBreakdown.length === 0 ? (
                    <tr>
                      <td className={classes.rankingCell} colSpan={2}>
                        Sem dados
                      </td>
                    </tr>
                  ) : (
                    metrics.queueBreakdown.map(row => (
                      <tr key={`${row.queueId ?? "none"}-${row.queueName}`}>
                        <td className={classes.rankingCell}>{row.queueName}</td>
                        <td className={classes.rankingCell}>{row.total}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.fixedHeightPaper}>
            <Typography component="h3" variant="h6" color="primary" paragraph>
              Ranking de Atendentes
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <table className={classes.rankingTable}>
                <thead>
                  <tr>
                    <th className={classes.rankingHeaderCell}>Atendente</th>
                    <th className={classes.rankingHeaderCell}>Total</th>
                    <th className={classes.rankingHeaderCell}>Fechados</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.ranking.length === 0 ? (
                    <tr>
                      <td className={classes.rankingCell} colSpan={3}>
                        Sem dados
                      </td>
                    </tr>
                  ) : (
                    metrics.ranking.map(row => (
                      <tr key={row.userId}>
                        <td className={classes.rankingCell}>{row.userName}</td>
                        <td className={classes.rankingCell}>{row.total}</td>
                        <td className={classes.rankingCell}>{row.closed}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.fixedHeightPaper}>
            <Typography component="h3" variant="h6" color="primary" paragraph>
              Atendimentos por Etiqueta
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <table className={classes.rankingTable}>
                <thead>
                  <tr>
                    <th className={classes.rankingHeaderCell}>Etiqueta</th>
                    <th className={classes.rankingHeaderCell}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.tagBreakdown.length === 0 ? (
                    <tr>
                      <td className={classes.rankingCell} colSpan={2}>
                        Sem dados
                      </td>
                    </tr>
                  ) : (
                    metrics.tagBreakdown.map((row, index) => (
                      <tr key={`${row.tagId ?? "none"}-${row.tagName ?? index}`}>
                        <td className={classes.rankingCell}>
                          {row.tagName || row.name || "Sem etiqueta"}
                        </td>
                        <td className={classes.rankingCell}>{row.total ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard;
